import { action, computed, makeObservable, observable } from 'mobx';
import { Award, Nomination, Organization } from '../../models';

export class ReviewAndSubmitNominationPageUiStore {
  @observable currentNomination?: Nomination;

  @observable currentAward?: Award;

  @observable isShowNoCountrySelectionModal = false;

  @observable isShowConfirmationModal = false;

  @observable expandedCustomerReferenceIds = new Set<number>();

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.currentNomination = undefined;
    this.currentAward = undefined;
    this.isShowNoCountrySelectionModal = false;
    this.isShowConfirmationModal = false;
    this.expandedCustomerReferenceIds.clear();
  }

  /* Nomination */

  @action
  setCurrentNomination(nomination: Nomination): void {
    this.currentNomination = nomination;
  }

  @computed
  get hasAssociatedCountries(): boolean {
    if (!this.currentNomination) return false;
    return (this.currentNomination.associatedCountries.length > 0 && 
            this.currentNomination.associatedCountries.every((country) => country.countryCode !== 'N/A'));
  }

  @computed
  get onlySentinelCountry(): boolean {
    if (!this.currentNomination) return false;
    return this.currentNomination.associatedCountries.length === 1 && this.currentNomination.associatedCountries[0].countryCode === 'N/A';
  }

  @computed
  get currentNominationOrganization(): Organization | undefined {
    return this.currentNomination?.organization ?? undefined;
  }

  /* Award */

  @action
  setCurrentAward(award: Award): void {
    this.currentAward = award;
  }

  @computed
  get isCountryAward(): boolean {
    return this.currentAward?.isCountryAward ?? false;
  }

  /* Modal */

  @action
  setShowNoCountrySelectionModal(isShow: boolean): void {
    this.isShowNoCountrySelectionModal = isShow;
  }

  @action
  setShowConfirmationModal(isShow: boolean): void {
    this.isShowConfirmationModal = isShow;
  }

  /* Customer reference description expansion */

  @action
  expandCustomerReferenceDescription(customerReferenceId: number): void {
    if (this.expandedCustomerReferenceIds.has(customerReferenceId)) return;
    const next = new Set(this.expandedCustomerReferenceIds);
    next.add(customerReferenceId);
    this.expandedCustomerReferenceIds = next;
  }

  @action
  collapseCustomerReferenceDescription(customerReferenceId: number): void {
    if (!this.expandedCustomerReferenceIds.has(customerReferenceId)) return;
    const next = new Set(this.expandedCustomerReferenceIds);
    next.delete(customerReferenceId);
    this.expandedCustomerReferenceIds = next;
  }

  isCustomerReferenceDescriptionExpanded(customerReferenceId: number): boolean {
    return this.expandedCustomerReferenceIds.has(customerReferenceId);
  }
}
